import { COLUMN_MINIMUM_PIXEL_WIDTH } from './constants';
import { sumBy } from 'lodash';
/**
 * Column widths are settable by the User, but it does not make sense to store pixel width (would change when
 * data table is resized), nor to store percentage width (would change when count of displayed columns changes)
 *
 * Instead we store 'width points' which are a relative measure - a column with 2x width points of another
 * column will receive 2x the space in the data table.
 *
 * This function takes a data table width and the current visible columns and returns the ratio of 'width points'
 * to pixels for those values.
 *
 * @param dataTableWidth
 * @param visibleColumns
 */
export var widthPointsToPixelWidthMultiplier = function (dataTableWidth, visibleColumns) {
    return (dataTableWidth / sumBy(visibleColumns, 'width'));
};
/**
 * Helper function that takes currently visible columns and the width of the Data Table container, finds
 * the smallest column and calculates its size if we would scale Data Table to fit exactly into the container.
 *
 * If this smallest column would be above minimum width then we can scale Data Table same size as container.
 * Or, if it would be too small, we must scale Data Table to be larger than its container (scrollbar will be shown).
 *
 * @param visibleColumns
 * @param containerWidth
 */
export var calculateDataTableWidth = function (visibleColumns, containerWidth) {
    var smallestColumnWidthPoints = visibleColumns[0].width;
    for (var i = 1; i < visibleColumns.length; i++) {
        var candidateWidthPoints = visibleColumns[i].width;
        if (candidateWidthPoints < smallestColumnWidthPoints) {
            smallestColumnWidthPoints = candidateWidthPoints;
        }
    }
    var widthIfResizedToFitContainer = smallestColumnWidthPoints * widthPointsToPixelWidthMultiplier(containerWidth, visibleColumns);
    if (widthIfResizedToFitContainer >= COLUMN_MINIMUM_PIXEL_WIDTH) {
        // Columns are all bigger than min width, data table will be same width as container
        return containerWidth;
    }
    else {
        // Scale data table width so that the smallest column will be exactly min width
        return containerWidth * (COLUMN_MINIMUM_PIXEL_WIDTH / widthIfResizedToFitContainer);
    }
};
/**
 * Helper function that takes collection of visible columns, width of data table and its container, and
 * the column index + pixel change that the User is currently trying to apply, and calculates new column sizes
 *
 * Supports the following rules specified by analysis:
 *
 * 1. No column may be smaller than minimum width
 * 2. When expanding a column, the size added to the column is subtracted from its right neighbour, UNLESS
 *        that neighbour is already minimum width, in which case expanding column expands total data table width
 * 3. When shrinking a column, the size subtracted from column is added to its right neighbour, UNLESS
 *        the data table is larger than its container (scrollbar is shown), in which case shrinking the column
 *        shrinks total data table width
 *
 *
 * Returns array of indices + new widths of any resized columns.
 *
 * @param visibleColumns
 * @param resizeIndex
 * @param deltaPixelResize
 * @param containerWidth
 * @param dataTableWidth
 */
export var calculateColumnResizes = function (visibleColumns, containerWidth, dataTableWidth, resizeIndex, deltaPixelResize) {
    var _a;
    var pixelsToPoints = widthPointsToPixelWidthMultiplier(dataTableWidth, visibleColumns);
    var shouldResizeNeighbour = shouldResizeNeighbourColumn(visibleColumns, resizeIndex + 1, -deltaPixelResize, containerWidth, dataTableWidth);
    var resizedWidth = getResizedColumnWidthPoints(visibleColumns[resizeIndex], deltaPixelResize, pixelsToPoints);
    if (resizedWidth === visibleColumns[resizeIndex].width) {
        return {}; // no change, return empty result
    }
    var result = (_a = {},
        _a[visibleColumns[resizeIndex].name] = resizedWidth,
        _a);
    if (shouldResizeNeighbour) {
        result[visibleColumns[resizeIndex + 1].name] = getResizedColumnWidthPoints(visibleColumns[resizeIndex + 1], -deltaPixelResize, pixelsToPoints);
    }
    return result;
};
var shouldResizeNeighbourColumn = function (columns, neighbourIndex, neighbourDeltaPixelResize, containerWidth, dataTableWidth) {
    var neighbourExists = neighbourIndex < columns.length;
    var neighbourWillShrink = neighbourDeltaPixelResize < 0;
    var dataTableHasSpaceForNeighbourToExpand = dataTableWidth <= containerWidth;
    return neighbourExists &&
        (neighbourWillShrink || dataTableHasSpaceForNeighbourToExpand);
};
var getResizedColumnWidthPoints = function (column, deltaPixelResize, pixelsToPoints) {
    var currentWidthPoints = column.width;
    var currentPixelWidth = currentWidthPoints * pixelsToPoints;
    var newPixelWidth = currentPixelWidth + deltaPixelResize;
    if (newPixelWidth < COLUMN_MINIMUM_PIXEL_WIDTH) {
        return currentWidthPoints;
    }
    else {
        return newPixelWidth / pixelsToPoints;
    }
};
//# sourceMappingURL=resizeUtils.js.map